import {
  type ExpandedState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Table from 'react-bootstrap/Table';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useIsScrollable } from '../../hooks/use-is-scrollable.hook';
import { SortBy } from '../../interfaces/sort-by.interface';
import { IMAGES_REPO } from '../../static-resources';
import { PreloaderComponent } from '../loading/preloader.component';
import './sortable-table.component.scss';

interface TableOptions {
  showTotal?: boolean;
  data: any[];
  columns: any[];
  getMore(): void;
  onSortBy?(sortByObject: SortBy[] | undefined): void;
  defaultSortBy?: SortBy[];
  loadMore: boolean | undefined;
  height?: number | string | undefined;
  className?: string;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  idField?: string;
  suppressRowClickSelection?: boolean;
  enableStickyFirstColumn?: boolean;
  expandRows?: boolean;
}

export const TableComponent = ({
  idField,
  onRowSelectionChange,
  rowSelection,
  enableRowSelection,
  suppressRowClickSelection = false,
  enableStickyFirstColumn = false,
  expandRows = false,
  enableMultiRowSelection,
  showTotal = false,
  data,
  columns,
  getMore,
  loadMore = false,
  height = undefined,
  onSortBy,
  defaultSortBy = [],
  className = '',
}: TableOptions) => {
  const [sortByObject, setSortByObject] = useState<SortingState>(
    defaultSortBy.map((i) => ({ desc: i.desc ?? false, id: i.id })),
  );
  useEffect(() => {
    if (defaultSortBy.length !== 0) {
      // Default sorting loaded only once.
      setSortByObject((value) =>
        value.length === 0
          ? defaultSortBy.map((i) => ({ desc: i.desc ?? false, id: i.id }))
          : value,
      );
    }
  }, [defaultSortBy]);
  const [expanded, setExpanded] = useState<ExpandedState>(
    expandRows !== undefined ? true : {},
  );

  const getRowIdFunction = useMemo(() => {
    if (!idField) return undefined;

    return (row: any, index: number, parent: any) => {
      // Use stableKey if available (for hierarchical data with parent-child relationships)
      if (row.stableKey) {
        return row.stableKey;
      }
      if (row[idField]) {
        return String(row[idField]);
      }
      if (row.id) {
        return String(row.id);
      }
      // Create unique ID that includes parent context to avoid conflicts
      const parentId = parent
        ? parent.id || parent.original?.id || `parent-${parent.index}`
        : '';
      return parentId ? `${parentId}-child-${index}` : `row-${index}`;
    };
  }, [idField]);

  const tableInstance = useReactTable({
    data,
    columns,
    state: {
      sorting: sortByObject,
      rowSelection: rowSelection ?? {},
      expanded,
    },
    getRowId: getRowIdFunction,
    onRowSelectionChange,
    enableRowSelection,
    enableMultiRowSelection,
    manualSorting: true,
    enableMultiSort: true,
    isMultiSortEvent: (e: any) => {
      return e.ctrlKey;
    },
    onSortingChange: setSortByObject,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: expandRows ? getExpandedRowModel() : undefined,
    getSubRows: expandRows ? (row) => row.children : undefined,
  });

  useEffect(() => {
    const s: SortBy[] = sortByObject.map((i) => i);
    if (onSortBy) onSortBy(s);
  }, [sortByObject, onSortBy]);

  const myref = useRef<InfiniteScroll | null>(null);
  const [isScrollable, ref, node] = useIsScrollable(data);
  useEffect(() => {
    const infScroll: any = myref.current;
    const scrollElement = infScroll.el;
    if (scrollElement && scrollElement !== node) {
      //console.log('GET Scroll Target', scrollElement)
      ref(scrollElement);
    }
  }, [myref, node, ref]);

  useEffect(() => {
    if (!node) return;
    if (!isScrollable && loadMore) {
      getMore();
    }
  }, [getMore, isScrollable, loadMore, node]);

  // dataLength must change when new data comes. Takes into account children. That way, InfiniteScroll can detect changes and call getMore function.
  const dataLength = useMemo(() => {
    const length = data.flatMap((i) => i.children ?? [i]).length;
    return length;
  }, [data]);

  return (
    <InfiniteScroll
      className="table-infinite-scroll"
      height={height}
      next={getMore}
      hasMore={loadMore}
      dataLength={dataLength}
      ref={myref}
      loader={
        <div className="table-infinite-scroll-loading">
          <PreloaderComponent />
        </div>
      }
    >
      <Table className={className}>
        {showTotal && (
          <tfoot>
            {tableInstance.getFooterGroups().map((footerGroup, index) => (
              <tr key={`footer-${index}-${footerGroup.id}`}>
                {footerGroup.headers.map((header, colIdx) => (
                  <td
                    key={`footer-cell-${header.id}-${colIdx}`}
                    colSpan={header.colSpan}
                    style={{
                      minWidth: header.getSize(),
                      textAlign: (header.column.columnDef.meta as any)?.align,
                    }}
                    className={[
                      (header.column.columnDef.meta as any)?.className ?? '',
                      enableStickyFirstColumn && colIdx === 0
                        ? 'sticky-dynamic-column sticky-column'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {flexRender(
                      header.column.columnDef.footer,
                      header.getContext(),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
        <thead>
          {tableInstance.getHeaderGroups().map((headerGroup, index) => (
            <tr
              key={`header-${index}-${headerGroup.id}`}
              className="sticky-thead"
            >
              {headerGroup.headers.map((header: any, colIdx: number) => (
                <th
                  key={`header-cell-${header.id}-${colIdx}`}
                  colSpan={header.colSpan}
                  className={[
                    (header.column.columnDef.meta as any)?.className ?? '',
                    colIdx === 0 && enableStickyFirstColumn
                      ? 'sticky-header-column sticky-dynamic-column'
                      : '',
                  ]
                    .filter((i) => i !== '')
                    .join(' ')}
                  style={{
                    minWidth: header.getSize(),
                    textAlign: (header.column.columnDef.meta as any)?.align,
                  }}
                >
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <div
                      className="cursor-pointer select-none header-sortable-text"
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          header.column.getToggleSortingHandler()?.(e);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Sort by ${header.column.columnDef.header}`}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <img src={IMAGES_REPO.ArrowUpIcon} alt="asc" />,
                        desc: (
                          <img src={IMAGES_REPO.ArrowDownIcon} alt="desc" />
                        ),
                      }[header.column.getIsSorted() as string] ?? null}
                      {header.column.getSortIndex() < 1 ? (
                        ''
                      ) : (
                        <Badge className="rounded-pill bg-secondary">
                          {header.column.getSortIndex() + 1}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <div>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map((row, rowIndex) => {
            // Create unique key that considers parent hierarchy
            let rowKey = row.id;
            if (!rowKey) {
              const parentPath = row
                .getParentRows()
                .map((parent) => parent.id || parent.index)
                .join('-');
              rowKey = parentPath
                ? `${parentPath}-${row.index || rowIndex}`
                : `row-${row.index || rowIndex}`;
            }

            return (
              <tr
                key={rowKey}
                id={row.id}
                className={[
                  `row-depth-${row.depth}`,
                  row.getIsSelected() ? 'selected' : '',
                  row?.original?.className ?? '',
                ]
                  .filter((i) => i !== '')
                  .join(' ')}
                onClick={
                  suppressRowClickSelection
                    ? undefined
                    : row.getToggleSelectedHandler()
                }
              >
                {row.getVisibleCells().map((cell, colIdx) => {
                  const cellKey = `${rowKey}-cell-${cell.column.id}-${colIdx}`;

                  return (
                    <td
                      key={cellKey}
                      className={[
                        (cell.column.columnDef.meta as any)?.className ?? '',
                        enableStickyFirstColumn && colIdx === 0
                          ? 'sticky-dynamic-column sticky-column'
                          : '',
                        row.getIsSelected() ? 'selected' : 'table-cell',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{
                        minWidth: cell.column.getSize(),
                        textAlign: (cell.column.columnDef.meta as any)?.align,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </InfiniteScroll>
  );
};
